import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useUser } from 'components/RDAuthContext';
import { useAdminContext } from '../../../../../context/AdminContext';
import {
  locationService,
  orgUnitService,
  metricsService,
  historyService,
  hierarchyService
} from '../services/domain';

/**
 * Main data fetching hook for LocationAdmin
 * Fetches minimal data needed for initial render
 */
export const useLocationAdminData = () => {
  const { user } = useUser();
  const { selectedClient } = useAdminContext();
  const clientId = selectedClient;

  return useQuery({
    queryKey: ['locationAdmin', clientId],
    queryFn: async () => {
      if (!clientId) {
        throw new Error('No client ID available');
      }

      // Fetch essential data for initial render

      try {
        // Fetch orgUnits first, then pass to locations for name mapping
        const [
          orgUnits,
          recentChanges,
          systemHealth,
          hierarchyDef
        ] = await Promise.all([
          orgUnitService.getOrgUnitsMaster(clientId),    // Just id, name, level
          historyService.getRecentChanges(clientId, 20),  // Get more recent changes
          metricsService.getSystemHealthIssues(clientId),
          hierarchyService.getHierarchyDefinition(clientId)  // Get hierarchy structure for proper labels
        ]);
        
        // Now fetch locations with orgUnits for name mapping
        const locations = await locationService.getLocationsMaster(clientId, orgUnits);

        // Compute counts client-side with hierarchy info
        const counts = computeLocationCounts(locations, orgUnits, hierarchyDef);

        return {
          locations,
          orgUnits,
          counts,
          recentChanges,
          systemHealth
        };
      } catch (error) {
        console.error('Failed to fetch LocationAdmin data:', error);
        throw error;
      }
    },
    enabled: !!clientId,
    staleTime: 5 * 60 * 1000,   // Consider data stale after 5 minutes
    gcTime: 30 * 60 * 1000,     // Keep in cache for 30 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 1,
  });
};

/**
 * Hook for fetching Structure tab data (lazy loaded)
 */
export const useHierarchyStructure = () => {
  const { user } = useUser();
  const { selectedClient } = useAdminContext();
  const clientId = selectedClient;

  return useQuery({
    queryKey: ['hierarchyStructure', clientId],
    queryFn: async () => {
      const [structure, version] = await Promise.all([
        hierarchyService.getHierarchyDefinition(clientId),
        hierarchyService.getCurrentHierarchyVersion(clientId)
      ]);
      return { structure, version };
    },
    enabled: !!clientId,
    staleTime: Infinity,        // Read-only data, cache forever
    gcTime: Infinity,
  });
};

/**
 * Hook for fetching Units tab data (lazy loaded)
 */
export const useOrgUnitsData = () => {
  const { user } = useUser();
  const { selectedClient } = useAdminContext();
  const clientId = selectedClient;

  return useQuery({
    queryKey: ['orgUnitsData', clientId],
    queryFn: async () => {
      const [activeUnits, inactiveUnits, orgUnitsByLevel] = await Promise.all([
        orgUnitService.getActiveOrgUnitsHierarchy(clientId),
        orgUnitService.getInactiveOrgUnitsHistory(clientId),
        orgUnitService.getOrgUnitsGroupedByLevel(clientId)
      ]);
      return { activeUnits, inactiveUnits, orgUnitsByLevel };
    },
    enabled: !!clientId,
    staleTime: 30 * 60 * 1000,  // 30 minutes
    gcTime: 60 * 60 * 1000,      // 1 hour
  });
};

/**
 * Hook for location updates with cache invalidation
 */
export const useUpdateLocation = () => {
  const queryClient = useQueryClient();
  const { user } = useUser();
  const { selectedClient } = useAdminContext();

  return useMutation({
    mutationFn: async ({ locationId, updates }) => {
      return await locationService.updateLocation(
        selectedClient,
        locationId,
        updates,
        user?.userName || user?.id
      );
    },
    onSuccess: (data, variables) => {
      // Invalidate main data to refresh counts if needed
      queryClient.invalidateQueries({ queryKey: ['locationAdmin'] });
      
      // Invalidate specific location details
      queryClient.invalidateQueries({ 
        queryKey: ['locationDetails', variables.locationId] 
      });
      
      // Invalidate history as a new entry was created
      queryClient.invalidateQueries({ queryKey: ['locationHistory'] });
    },
    onError: (error) => {
      console.error('Failed to update location:', error);
    }
  });
};

/**
 * Hook for manager updates
 */
export const useUpdateManager = () => {
  const queryClient = useQueryClient();
  const { user } = useUser();
  const { selectedClient } = useAdminContext();

  return useMutation({
    mutationFn: async ({ locationId, managerUpdates }) => {
      // Assuming managerService.updateLocationManager exists
      return await locationService.updateLocationManager(
        selectedClient,
        locationId,
        managerUpdates,
        user?.userName || user?.id
      );
    },
    onSuccess: (data, variables) => {
      // Invalidate to refresh the locations list (manager info might be shown)
      queryClient.invalidateQueries({ queryKey: ['locationAdmin'] });
      
      // Invalidate location details
      queryClient.invalidateQueries({ 
        queryKey: ['locationDetails', variables.locationId] 
      });
    }
  });
};

/**
 * Compute location counts by organization level
 */
function computeLocationCounts(locations = [], orgUnits = [], hierarchyDef = []) {
  const counts = {
    totalLocations: locations.length,
    activeLocations: 0,
    inactiveLocations: 0,
    unknownStatusLocations: 0,
    byOrgLevel: {},
    orgUnitBreakdown: [],
    hierarchyHealth: 95,  // Default value - could be computed based on actual issues
    issues: 0  // Default value - could be computed based on actual issues
  };

  // Count by status
  locations.forEach(loc => {
    if (loc.status === 'Active' || !loc.closeDate) {
      counts.activeLocations++;
    } else if (loc.status === 'Closed' || loc.closeDate) {
      counts.inactiveLocations++;
    } else {
      counts.unknownStatusLocations++;
    }

    // Count by org level
    if (loc.orgLevel_1) {
      if (!counts.byOrgLevel[loc.orgLevel_1]) {
        counts.byOrgLevel[loc.orgLevel_1] = 0;
      }
      counts.byOrgLevel[loc.orgLevel_1]++;
    }
  });

  // Create org unit breakdown by level for the Total Units metric card
  // Group org units by level and count them
  const unitsByLevel = {};
  orgUnits.forEach(unit => {
    if (!unitsByLevel[unit.level]) {
      unitsByLevel[unit.level] = 0;
    }
    unitsByLevel[unit.level]++;
  });
  
  // Use hierarchy definition to get the actual org levels and labels
  // Filter to only include ORGLEVEL_ levels (not BRAND or LOCATION)
  const orgLevels = (hierarchyDef || [])
    .filter(level => level && level.levelId && level.levelId.startsWith('ORGLEVEL_'))
    .sort((a, b) => (b.order || 0) - (a.order || 0)); // Sort by order descending (3, 2, 1)
  
  // Create breakdown only for levels that exist in the hierarchy
  counts.orgUnitBreakdown = orgLevels.map(level => {
    // Create a plural label from the level label
    let pluralLabel = level.label || level.name || level.levelId;
    
    // Simple pluralization (you can make this more sophisticated if needed)
    if (!pluralLabel.endsWith('s')) {
      pluralLabel = pluralLabel.toLowerCase() + 's';
    } else {
      pluralLabel = pluralLabel.toLowerCase();
    }
    
    return {
      count: unitsByLevel[level.levelId] || 0,
      label: pluralLabel
    };
  });

  // Overall org unit counts
  counts.orgUnits = orgUnits.length;

  // Count issues (units without managers, locations without proper assignment, etc.)
  let issueCount = 0;
  
  // Count org units without managers (if manager info is available)
  const unitsWithoutManagers = orgUnits.filter(unit => !unit.managerId).length;
  if (unitsWithoutManagers > 0) issueCount += unitsWithoutManagers;
  
  // Count locations without org assignment
  const locationsWithoutOrg = locations.filter(loc => !loc.orgLevel_1).length;
  if (locationsWithoutOrg > 0) issueCount += locationsWithoutOrg;
  
  counts.issues = issueCount;
  
  // Calculate hierarchy health (simple percentage based on issues)
  if (locations.length > 0) {
    const healthScore = Math.max(0, 100 - (issueCount * 5));  // Deduct 5% per issue
    counts.hierarchyHealth = Math.min(100, healthScore);
  }

  return counts;
}

/**
 * Hook for refreshing all data
 */
export const useRefreshAll = () => {
  const queryClient = useQueryClient();
  const { user } = useUser();
  const { selectedClient } = useAdminContext();

  return () => {
    // Trigger manual refresh
    queryClient.invalidateQueries({
      queryKey: ['locationAdmin', selectedClient]
    });
  };
};